import { useState, useEffect, useCallback } from 'react'
import {
  Home, FolderOpen, Folder, ChevronRight,
  RefreshCw, Loader2, ArrowLeft,
  HardDrive, Clock, Shield, Tag,
} from 'lucide-react'
import { getFileIcon } from '../utils/fileIcons'
import { get, getBaseUrl } from '../utils/api'
import WebsiteStatsCard from './WebsiteStatsCard'

function panelUrl(domain) {
  const origin = getBaseUrl().replace(/\/api\/v1\/?$/, '')
  return `${origin}/websites/${encodeURIComponent(domain)}/files`
}

function fmt(bytes) {
  if (!bytes || bytes === 0) return '—'
  if (bytes < 1024)       return bytes + ' B'
  if (bytes < 1048576)    return (bytes / 1024).toFixed(1) + ' KB'
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB'
  return (bytes / 1073741824).toFixed(2) + ' GB'
}

function fmtDate(val) {
  if (!val) return '—'
  const d = new Date(val)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/* ─── Accordion section ─── */
function Accordion({ label, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-2 pt-3 pb-1 text-[10px] font-medium text-white/25 uppercase tracking-widest hover:text-white/40 transition-colors"
      >
        {label}
        <ChevronRight size={10} className={`transition-transform duration-150 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && children}
    </div>
  )
}

/* ─── File details panel (shown when a file is open) ─── */
function FileDetails({ file, onBack }) {
  const { icon, color } = getFileIcon(file.name, 14)

  const details = [
    { icon: <HardDrive size={12} />, label: 'Size',        value: fmt(file.size) },
    { icon: <Tag size={12} />,       label: 'Type',        value: file.mime_type ?? '—' },
    { icon: <Shield size={12} />,    label: 'Permissions', value: file.permissions ?? '—' },
    { icon: <Clock size={12} />,     label: 'Modified',    value: fmtDate(file.modified_at) },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* Back */}
      <div className="px-3 pt-2 pb-1 shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-white/50 hover:text-white text-xs px-2 py-1.5 rounded-lg hover:bg-white/[0.06] transition-colors w-full"
        >
          <ArrowLeft size={14} />
          <span>Back</span>
        </button>
      </div>

      {/* File identity */}
      <div className="px-4 pt-1 pb-3 border-b border-white/[0.06]">
        <div className={`mb-2 ${color}`}>{icon}</div>
        <p className="text-[11px] font-semibold text-white/80 break-all leading-snug">{file.name}</p>
      </div>

      {/* Accordions */}
      <div className="flex-1 overflow-y-auto sidebar-scroll px-2 pb-4">
        <Accordion label="Properties" defaultOpen={true}>
          {details.map(d => (
            <div key={d.label} className="flex items-start gap-2.5 px-3 py-2 rounded-lg">
              <span className="shrink-0 text-white/30 mt-0.5">{d.icon}</span>
              <div className="min-w-0">
                <p className="text-[10px] text-white/30 leading-none mb-0.5">{d.label}</p>
                <p className="text-[11px] text-white/70 break-all leading-snug">{d.value}</p>
              </div>
            </div>
          ))}
        </Accordion>

        <Accordion label="Path" defaultOpen={true}>
          <div className="px-3 py-2">
            <p className="text-[10px] text-white/50 font-mono break-all leading-relaxed">{file.path}</p>
          </div>
        </Accordion>
      </div>
    </div>
  )
}

/* ─── Folder tree node ─── */
function TreeNode({ domain, entry, currentPath, onNavigate, depth = 0 }) {
  const [open, setOpen]         = useState(false)
  const [children, setChildren] = useState([])
  const [loading, setLoading]   = useState(false)

  const isActive = currentPath === entry.path || currentPath.startsWith(entry.path + '/')

  const handleToggle = async (e) => {
    e.stopPropagation()
    if (!open && children.length === 0) {
      setLoading(true)
      try {
        const r = await get(`/websites/${domain}/files`, { path: entry.path })
        const entries = r?.data?.entries ?? r?.data ?? r ?? []
        setChildren(entries.filter(e => e.type === 'directory'))
      } catch {}
      setLoading(false)
    }
    setOpen(o => !o)
  }

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 pr-3 rounded-lg cursor-pointer transition-all ${
          isActive
            ? 'bg-white/15 text-white'
            : 'text-white/60 hover:bg-white/[0.08] hover:text-white'
        }`}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
        onClick={() => onNavigate(entry.path)}
      >
        <span
          onClick={handleToggle}
          className="p-1.5 shrink-0 opacity-50 hover:opacity-100 transition-opacity"
        >
          {loading
            ? <Loader2 size={10} className="animate-spin" />
            : <ChevronRight
                size={10}
                className={`transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
              />
          }
        </span>
        <span className="shrink-0 opacity-70">
          {isActive
            ? <FolderOpen size={13} className="text-amber-400" />
            : <Folder size={13} className="text-amber-400/70" />
          }
        </span>
        <span className={`flex-1 text-xs truncate py-2 ${isActive ? 'font-medium' : ''}`}>
          {entry.name}
        </span>
        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-white/60 shrink-0" />}
      </div>

      {open && children.length > 0 && (
        <div>
          {children.map(child => (
            <TreeNode
              key={child.path}
              domain={domain}
              entry={child}
              currentPath={currentPath}
              onNavigate={onNavigate}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Main FileSidebar ─── */
export default function FileSidebar({ domain, currentPath, onNavigate, activeFile, onCloseFile }) {
  const [rootEntries, setRootEntries] = useState([])
  const [loading, setLoading]         = useState(true)

  const loadRoot = useCallback(async () => {
    setLoading(true)
    try {
      const r = await get(`/websites/${domain}/files`, { path: '/' })
      const entries = r?.data?.entries ?? r?.data ?? r ?? []
      setRootEntries(entries.filter(e => e.type === 'directory'))
    } catch {}
    setLoading(false)
  }, [domain])

  useEffect(() => { if (domain) loadRoot() }, [domain, loadRoot])

  return (
    <div className="flex flex-col h-full bg-ink shrink-0 overflow-hidden gap-3 p-3" style={{ width: 240 }}>
      {/* Brand card — solid white, matching the main panel's sidebar */}
      <div className="flex h-14 shrink-0 items-center justify-between px-3 rounded-2xl bg-white shadow-sm">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <FolderOpen size={15} className="text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-900 truncate leading-tight">File Manager</p>
            <p className="text-[10px] text-gray-500 truncate leading-tight mt-0.5">{domain}</p>
          </div>
        </div>
      </div>

      {/* Body — switches between directory tree and file details */}
      {activeFile ? (
        <FileDetails file={activeFile} onBack={onCloseFile} />
      ) : (
        <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5 sidebar-scroll">

          {/* Home shortcut */}
          <button
            type="button"
            onClick={() => onNavigate('/')}
            className={`w-full flex items-center px-3 py-2.5 rounded-lg transition-all text-xs ${
              currentPath === '/'
                ? 'bg-primary text-white font-medium'
                : 'text-white/60 hover:bg-white/[0.08] hover:text-white'
            }`}
          >
            <Home size={16} className="shrink-0" />
            <span className="ml-3 font-medium">Home</span>
          </button>

          {/* Directory tree */}
          <div className="flex items-center justify-between pt-3 pb-1 px-2">
            <p className="text-[10px] font-medium text-white/25 uppercase tracking-widest">
              Directories
            </p>
            <button
              type="button"
              onClick={loadRoot}
              title="Refresh"
              className="text-white/25 hover:text-white/60 transition-colors"
            >
              {loading
                ? <Loader2 size={10} className="animate-spin" />
                : <RefreshCw size={10} />
              }
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={14} className="animate-spin text-white/30" />
            </div>
          ) : rootEntries.length === 0 ? (
            <p className="px-3 py-2 text-[10px] text-white/25">No directories</p>
          ) : (
            <nav className="space-y-0.5">
              {rootEntries.map(entry => (
                <TreeNode
                  key={entry.path}
                  domain={domain}
                  entry={entry}
                  currentPath={currentPath}
                  onNavigate={onNavigate}
                />
              ))}
            </nav>
          )}
        </div>
      )}

      {/* Stats card — solid white, matching the main panel's bottom user card */}
      <div className="shrink-0 rounded-2xl bg-white shadow-sm p-3 space-y-2.5">
        <WebsiteStatsCard domain={domain} />
        <p className="text-[9px] text-gray-400 truncate font-mono px-0.5" title={currentPath}>
          {activeFile ? activeFile.path : currentPath}
        </p>
        <a
          href={panelUrl(domain)}
          className="flex items-center gap-2 text-[11px] font-medium text-gray-500 hover:text-primary transition-colors px-0.5"
        >
          <ArrowLeft size={12} /> Back to Panel
        </a>
      </div>
    </div>
  )
}
